import { useMemo } from "react";
import { returnSwapLegDefaults } from "@/lib/businessRules/swapConventionsRule";
import { TradeInterestType } from "@/lib/interfaces and Enums/ENUMSwapTradeMessage";
import tradeFromScratchStore, {
  ISwapLegTicket,
  ISwaptionTicket,
  returnEmptySwaptionTicket,
} from "@/store/tradeFromScratchStore";
import { SwapLegKey } from "../helper/swapTicketHelper";
import {
  returnMissingSwaptionTicketEntries,
  returnRequiredSwaptionTicketKeys,
  returnSwaptionDateIssue,
} from "../helper/swaptionTicketHelper";

// One swaption ticket by id: the stored ticket, its updaters and what derives from it.
export function useSwaptionTicket(ticketId: string) {
  const storedTicket = tradeFromScratchStore(
    (state) => state.swaptionTicketsByTicketId[ticketId],
  );
  const updateTicket = tradeFromScratchStore(
    (state) => state.updateSwaptionTicket,
  );
  const updateLeg = tradeFromScratchStore((state) => state.updateSwaptionLeg);

  const emptyTicket = useMemo(() => returnEmptySwaptionTicket(), []);
  const ticket = storedTicket ?? emptyTicket;

  const updateSwaptionLeg = (
    leg: SwapLegKey,
    changes: Partial<ISwapLegTicket>,
  ) => updateLeg(ticketId, leg, changes);

  // Same rule as the swap ticket, minus spread: the swaption leg model has none.
  const handleLegConventionChange = (
    legKey: SwapLegKey,
    changes: { currency?: string; interestType?: TradeInterestType },
  ) => {
    const leg = ticket[legKey];
    const currency = changes.currency ?? leg.currency;
    const interestType = changes.interestType ?? leg.interestType;
    updateSwaptionLeg(legKey, {
      currency,
      interestType,
      ...returnSwapLegDefaults(currency, interestType),
      couponRate:
        interestType === TradeInterestType.Fixed ? leg.couponRate : null,
    });
    // The legs no longer match the picked template.
    if (ticket.templateName) updateTicket(ticketId, { templateName: "" });
  };

  return {
    ticket,
    updateSwaptionTicket: (changes: Partial<ISwaptionTicket>) =>
      updateTicket(ticketId, changes),
    updateSwaptionLeg,
    handleLegConventionChange,
    missingEntries: returnMissingSwaptionTicketEntries(ticket),
    dateIssue: returnSwaptionDateIssue(ticket),
    requiredKeys: returnRequiredSwaptionTicketKeys(ticket),
  };
}
